import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  User,
  ArrowLeft,
  Zap,
  Target,
  Trophy,
  Calendar,
  Mail,
  BarChart2,
  Eye,
  CheckCircle,
  XCircle,
  ExternalLink,
  Clock,
  Award,
  Medal,
  Swords,
  Star,
  Crown,
  TrendingUp,
  Download,
  X,
  Sun,
  Moon,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

// ─── Dark Mode Hook ───────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  useEffect(() => { localStorage.setItem("tg-theme", dark ? "dark" : "light"); }, [dark]);
  return [dark, setDark];
}

// ─── Level Info ───────────────────────────────────────────────────────────────
const LEVEL_INFO = [
  { min: 0,     max: 500,    name: "Bronze Tax Scholar",      emoji: "🥉", color: "#ea580c" },
  { min: 500,   max: 1500,   name: "Silver Tax Analyst",      emoji: "🥈", color: "#64748b" },
  { min: 1500,  max: 3500,   name: "Gold Tax Advisor",        emoji: "🥇", color: "#d97706" },
  { min: 3500,  max: 7000,   name: "Platinum Tax Strategist", emoji: "💎", color: "#4f46e5" },
  { min: 7000,  max: 999999, name: "Diamond Tax Master",      emoji: "👑", color: "#0891b2" },
];
const getLevel = (gp) => LEVEL_INFO.find(l => gp >= l.min && gp < l.max) || LEVEL_INFO[0];

// ─── Certificate themes — one distinct palette per level ─────────────────────
const CERT_THEMES = {
  bronze: {
    bg1: "#3b1508", bg2: "#7c2d12", bg3: "#c2410c",
    panel: "rgba(255,247,237,0.10)",
    accent: "#fb923c", accent2: "#fed7aa",
    border: "#f97316", borderLight: "#fdba74",
    badge: "🥉", star: "#fb923c",
  },
  silver: {
    bg1: "#0c111d", bg2: "#111827", bg3: "#1f2937",
    panel: "rgba(248,250,252,0.08)",
    accent: "#cbd5e1", accent2: "#f1f5f9",
    border: "#94a3b8", borderLight: "#e2e8f0",
    badge: "🥈", star: "#94a3b8",
  },
  gold: {
    bg1: "#1c0a00", bg2: "#451a03", bg3: "#92400e",
    panel: "rgba(255,251,235,0.10)",
    accent: "#fbbf24", accent2: "#fef3c7",
    border: "#f59e0b", borderLight: "#fde68a",
    badge: "🥇", star: "#fbbf24",
  },
  platinum: {
    bg1: "#0d0b2e", bg2: "#1e1b4b", bg3: "#3730a3",
    panel: "rgba(238,242,255,0.08)",
    accent: "#818cf8", accent2: "#e0e7ff",
    border: "#6366f1", borderLight: "#a5b4fc",
    badge: "💎", star: "#818cf8",
  },
  diamond: {
    bg1: "#021017", bg2: "#082f49", bg3: "#0c4a6e",
    panel: "rgba(236,254,255,0.08)",
    accent: "#22d3ee", accent2: "#cffafe",
    border: "#0891b2", borderLight: "#67e8f9",
    badge: "👑", star: "#22d3ee",
  },
  legend: {
    bg1: "#0d0005", bg2: "#3b0010", bg3: "#881337",
    panel: "rgba(255,228,230,0.08)",
    accent: "#fb7185", accent2: "#ffe4e6",
    border: "#e11d48", borderLight: "#fda4af",
    badge: "🔱", star: "#fb7185",
  },
};

// Draw a 6-pointed star ornament
function drawStar(ctx, cx, cy, r, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3 - Math.PI / 2;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    const innerAngle = angle + Math.PI / 6;
    ctx.lineTo(cx + (r * 0.45) * Math.cos(innerAngle), cy + (r * 0.45) * Math.sin(innerAngle));
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawCertificate(canvas, certName, level, userName, earnedDate) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;   // 800
  const H = canvas.height;  // 680
  const t = CERT_THEMES[level] || CERT_THEMES.gold;

  // ── 1. Rich dark background gradient ──────────────────────────────────────
  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0,   t.bg1);
  bgGrad.addColorStop(0.5, t.bg2);
  bgGrad.addColorStop(1,   t.bg3);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Subtle radial glow in centre
  const glow = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 380);
  glow.addColorStop(0, t.accent + "22");
  glow.addColorStop(1, "transparent");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // ── 2. Outer decorative border (double line) ───────────────────────────────
  ctx.strokeStyle = t.border;
  ctx.lineWidth = 6;
  ctx.strokeRect(18, 18, W - 36, H - 36);
  ctx.strokeStyle = t.borderLight + "55";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(30, 30, W - 60, H - 60);

  // ── 3. Corner star ornaments ───────────────────────────────────────────────
  [[52, 52], [W - 52, 52], [52, H - 52], [W - 52, H - 52]].forEach(([cx, cy]) => {
    drawStar(ctx, cx, cy, 14, t.star + "cc");
  });

  // ── 4. Header strip ────────────────────────────────────────────────────────
  const headerGrad = ctx.createLinearGradient(0, 58, 0, 130);
  headerGrad.addColorStop(0, t.accent + "33");
  headerGrad.addColorStop(1, "transparent");
  ctx.fillStyle = headerGrad;
  ctx.fillRect(38, 58, W - 76, 72);

  // Thin accent line under header
  ctx.strokeStyle = t.accent;
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(60, 130); ctx.lineTo(W - 60, 130); ctx.stroke();

  // Brand name
  ctx.fillStyle = t.accent2;
  ctx.font = "bold 13px 'Nunito', Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.letterSpacing = "0.18em";
  ctx.fillText("TAX GENIUS  ·  taxgenius.online", W / 2, 82);

  // "CERTIFICATE OF ACHIEVEMENT"
  ctx.fillStyle = t.accent;
  ctx.font = "bold 22px Georgia, 'Times New Roman', serif";
  ctx.fillText("CERTIFICATE OF ACHIEVEMENT", W / 2, 116);

  // ── 5. "This certifies that" ───────────────────────────────────────────────
  ctx.fillStyle = t.accent2 + "aa";
  ctx.font = "italic 16px Georgia, serif";
  ctx.fillText("This certifies that", W / 2, 166);

  // ── 6. Recipient name ──────────────────────────────────────────────────────
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 46px Georgia, 'Times New Roman', serif";
  ctx.fillText(userName, W / 2, 228);

  // Name underline (accent colour)
  const nameW = ctx.measureText(userName).width;
  ctx.strokeStyle = t.accent;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - nameW / 2, 240);
  ctx.lineTo(W / 2 + nameW / 2, 240);
  ctx.stroke();

  // ── 7. Achievement text ────────────────────────────────────────────────────
  ctx.fillStyle = t.accent2 + "cc";
  ctx.font = "16px Georgia, serif";
  ctx.fillText("has demonstrated mastery in", W / 2, 278);

  ctx.fillStyle = t.accent2;
  ctx.font = "bold 18px Georgia, serif";
  ctx.fillText("Indian Income Tax Law · Old Act 1961 vs New Act 2025", W / 2, 306);

  // ── 8. Central badge area ──────────────────────────────────────────────────
  // Glow circle behind badge
  const badgeGlow = ctx.createRadialGradient(W / 2, 376, 10, W / 2, 376, 56);
  badgeGlow.addColorStop(0, t.accent + "44");
  badgeGlow.addColorStop(1, "transparent");
  ctx.fillStyle = badgeGlow;
  ctx.beginPath(); ctx.arc(W / 2, 376, 56, 0, Math.PI * 2); ctx.fill();

  // Badge emoji
  ctx.font = "64px serif";
  ctx.textAlign = "center";
  ctx.fillText(t.badge, W / 2, 400);

  // Certificate name
  ctx.fillStyle = t.accent;
  ctx.font = "bold 28px Georgia, 'Times New Roman', serif";
  ctx.fillText(certName, W / 2, 448);

  // Thin dividers flanking cert name
  const cw = ctx.measureText(certName).width;
  ctx.strokeStyle = t.border + "66";
  ctx.lineWidth = 1;
  [[W / 2 - cw / 2 - 60, W / 2 - cw / 2 - 12], [W / 2 + cw / 2 + 12, W / 2 + cw / 2 + 60]].forEach(([x1, x2]) => {
    ctx.beginPath(); ctx.moveTo(x1, 440); ctx.lineTo(x2, 440); ctx.stroke();
  });

  // ── 9. Footer area ─────────────────────────────────────────────────────────
  ctx.strokeStyle = t.accent + "44";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(60, 490); ctx.lineTo(W - 60, 490); ctx.stroke();

  // Issued date (left) · A project by AI-for-India (center) · website (right)
  ctx.font = "13px 'Nunito', Arial, sans-serif";

  ctx.fillStyle = t.accent2 + "99";
  ctx.textAlign = "left";
  ctx.fillText(`Issued: ${earnedDate}`, 70, 520);

  ctx.textAlign = "center";
  ctx.fillStyle = t.accent + "bb";
  ctx.fillText("A project by AI-for-India", W / 2, 520);

  ctx.textAlign = "right";
  ctx.fillStyle = t.accent2 + "99";
  ctx.fillText("taxgenius.online", W - 70, 520);

  // Verification seal (bottom centre)
  const sealX = W / 2;
  const sealY = 596;
  const sealR = 42;

  ctx.beginPath(); ctx.arc(sealX, sealY, sealR, 0, Math.PI * 2);
  ctx.fillStyle = t.accent + "18"; ctx.fill();
  ctx.strokeStyle = t.accent; ctx.lineWidth = 2; ctx.stroke();

  ctx.beginPath(); ctx.arc(sealX, sealY, sealR - 8, 0, Math.PI * 2);
  ctx.strokeStyle = t.accent + "55"; ctx.lineWidth = 1; ctx.stroke();

  ctx.font = "26px serif";
  ctx.textAlign = "center";
  ctx.fillText("✦", sealX, sealY + 9);

  // "VERIFIED" text around seal
  ctx.fillStyle = t.accent + "88";
  ctx.font = "bold 9px 'Nunito', Arial, sans-serif";
  ctx.fillText("· VERIFIED · TAXGENIUS.ONLINE ·", sealX, sealY + sealR + 16);

  // Small star ornaments flanking seal
  drawStar(ctx, sealX - 80, sealY, 7, t.star + "88");
  drawStar(ctx, sealX + 80, sealY, 7, t.star + "88");
}

// ─── All certificate levels ───────────────────────────────────────────────────
const ALL_CERTS = [
  { level: "bronze",   name: "Bronze Tax Scholar",        threshold: 100,  emoji: "🥉", desc: "Answer 100 unique questions" },
  { level: "silver",   name: "Silver Tax Analyst",        threshold: 250,  emoji: "🥈", desc: "Answer 250 unique questions" },
  { level: "gold",     name: "Gold Tax Advisor",          threshold: 500,  emoji: "🥇", desc: "Answer 500 unique questions" },
  { level: "platinum", name: "Platinum Tax Strategist",   threshold: 750,  emoji: "💎", desc: "Answer 750 unique questions" },
  { level: "diamond",  name: "Diamond Tax Master",        threshold: 1000, emoji: "👑", desc: "Master the full question bank!" },
  { level: "legend",   name: "Legend Tax Grandmaster",    threshold: 2000, emoji: "🔱", desc: "Answer 2000 unique questions — become a legend!" },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [D, setDark] = useDarkMode();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizDetail, setQuizDetail] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [certModal, setCertModal] = useState(null); // { cert, earnedDate }
  const certCanvasRef = useRef(null);

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const bg       = D ? "#0f172a" : "#f0fdf4";
  const surface  = D ? "#1e293b" : "#ffffff";
  const surface2 = D ? "#253347" : "#f8fafc";
  const border   = D ? "#334155" : "#e2e8f0";
  const textPri  = D ? "#f1f5f9" : "#1e293b";
  const textSec  = D ? "#94a3b8" : "#64748b";

  useEffect(() => {
    fetchStats();
    fetchProgress();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
      }
    } catch (error) {
      console.error("Failed to fetch progress:", error);
    }
  };

  const fetchQuizDetail = async (quizId) => {
    if (selectedQuiz === quizId) {
      setSelectedQuiz(null);
      setQuizDetail(null);
      return;
    }
    setLoadingQuiz(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/quiz-history/${quizId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuizDetail(data);
        setSelectedQuiz(quizId);
      }
    } catch (error) {
      console.error("Failed to fetch quiz detail:", error);
    } finally {
      setLoadingQuiz(false);
    }
  };

  const openCertModal = (cert) => {
    const earnedDate = new Date().toLocaleDateString("en-IN", {
      year: "numeric", month: "long", day: "numeric",
    });
    setCertModal({ cert, earnedDate });
    setTimeout(() => {
      const canvas = certCanvasRef.current;
      if (canvas) {
        drawCertificate(canvas, cert.name, cert.level, user?.name || "Tax Genius User", earnedDate);
      }
    }, 100);
  };

  const downloadCertificate = () => {
    const canvas = certCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `TaxGenius_${certModal.cert.name.replace(/\s+/g, "_")}_Certificate.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const totalGP = stats?.total_gp || 0;
  const level = getLevel(totalGP);
  const accuracy = stats?.accuracy || 0;
  const uniqueAnswered = progress?.unique_questions_answered || 0;

  // Accuracy ring SVG params
  const ringR = 54;
  const ringC = 2 * Math.PI * ringR;
  const ringOffset = ringC - (accuracy / 100) * ringC;

  // Next cert progress
  const earnedLevels = new Set((progress?.certificates_earned || []).map(c => c.level));
  const nextCertDef = ALL_CERTS.find(c => !earnedLevels.has(c.level));
  const nextCertProgress = nextCertDef
    ? Math.min(100, Math.round((uniqueAnswered / nextCertDef.threshold) * 100))
    : 100;

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>

      {/* ── Sticky Header ──────────────────────────────────────────────────── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 50,
        background: surface, borderBottom: `1px solid ${border}`,
        boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
        transition: "background 0.3s, border-color 0.3s",
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 38, height: 38, background: "linear-gradient(135deg, #059669, #0d9488)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontWeight: 900, fontSize: 18 }}>T</span>
            </div>
            <span style={{ fontWeight: 900, fontSize: 20, color: textPri, letterSpacing: -0.3 }}>Tax Genius</span>
          </div>

          {/* Right controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 10,
                border: `1px solid ${border}`, cursor: "pointer",
                fontSize: 14, fontWeight: 700,
                background: surface2, color: textSec,
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              <ArrowLeft size={16} />
              Dashboard
            </button>
            <button
              onClick={() => setDark(!D)}
              style={{
                width: 38, height: 38, borderRadius: "50%",
                background: D ? "#334155" : "#f1f5f9",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {D ? <Sun size={17} color="#fbbf24" /> : <Moon size={17} color="#64748b" />}
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "32px 16px 80px" }}>

        {/* ── Hero Profile Card ───────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28 }}>
          <div style={{
            borderRadius: 24, overflow: "hidden",
            boxShadow: D ? "0 4px 32px rgba(0,0,0,0.4)" : "0 4px 24px rgba(0,0,0,0.10)",
          }}>
            {/* Banner */}
            <div style={{
              background: D
                ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                : "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
              padding: "32px 28px 28px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                {/* Avatar */}
                <div style={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: "linear-gradient(135deg, #10b981, #0d9488)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 32, fontWeight: 900, color: "white",
                  border: "3px solid rgba(255,255,255,0.3)",
                  flexShrink: 0,
                }}>
                  {(user?.name || "T").charAt(0).toUpperCase()}
                </div>

                <div style={{ flex: 1 }}>
                  <h1 style={{ color: "#ffffff", fontSize: 26, fontWeight: 900, margin: "0 0 6px", letterSpacing: -0.5 }}>
                    {user?.name || "User"}
                  </h1>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.8)", fontSize: 14, marginBottom: 4 }}>
                    <span>✉️</span>
                    <span>{user?.email}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                    <span>📅</span>
                    <span>Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long" }) : "—"}</span>
                  </div>
                </div>

                {/* Badges */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                  {/* Level badge */}
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(255,255,255,0.15)", borderRadius: 100,
                    padding: "6px 14px", border: "1px solid rgba(255,255,255,0.25)",
                  }}>
                    <span style={{ fontSize: 16 }}>{level.emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#ffffff" }}>{level.name}</span>
                  </div>
                  {/* Rank badge */}
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(255,255,255,0.15)", borderRadius: 100,
                    padding: "6px 14px", border: "1px solid rgba(255,255,255,0.25)",
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#ffffff" }}>
                      🏆 #{stats?.rank || "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* 4 mini stat chips */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 24 }}>
                {[
                  { label: "Total GP", value: loading ? "…" : totalGP.toLocaleString(), emoji: "⚡" },
                  { label: "Quizzes", value: loading ? "…" : (stats?.quizzes_taken || 0), emoji: "📝" },
                  { label: "Questions", value: loading ? "…" : (stats?.total_questions_answered || (stats?.quizzes_taken || 0) * 15), emoji: "🧠" },
                  { label: "Accuracy", value: loading ? "…" : `${accuracy}%`, emoji: "🎯" },
                ].map((chip, i) => (
                  <div key={i} style={{
                    background: "rgba(255,255,255,0.12)",
                    borderRadius: 14, padding: "10px 8px", textAlign: "center",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}>
                    <div style={{ fontSize: 18, marginBottom: 2 }}>{chip.emoji}</div>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#ffffff" }}>{chip.value}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 1 }}>{chip.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Tab Navigation ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { id: "overview", label: "Overview" },
            { id: "achievements", label: "Achievements" },
            { id: "history", label: "Quiz History" },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`profile-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "9px 22px", borderRadius: 100,
                border: activeTab === tab.id ? "none" : `1.5px solid ${border}`,
                background: activeTab === tab.id ? "#10b981" : "transparent",
                color: activeTab === tab.id ? "#ffffff" : textSec,
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                fontFamily: "'Nunito', sans-serif",
                transition: "background 0.2s, color 0.2s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            OVERVIEW TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "overview" && (
          <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Accuracy Ring */}
            <div style={{
              background: surface, borderRadius: 20, padding: "28px 24px",
              border: `1px solid ${border}`, textAlign: "center",
              boxShadow: D ? "0 2px 16px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
            }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: textPri, marginBottom: 20 }}>
                🎯 Overall Accuracy
              </div>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <svg width={140} height={140} style={{ transform: "rotate(-90deg)" }}>
                  <circle cx={70} cy={70} r={ringR} fill="none" stroke={border} strokeWidth={12} />
                  <circle
                    cx={70} cy={70} r={ringR}
                    fill="none" stroke="#10b981" strokeWidth={12}
                    strokeLinecap="round"
                    strokeDasharray={ringC}
                    strokeDashoffset={ringOffset}
                    style={{ transition: "stroke-dashoffset 1s ease" }}
                  />
                  <text x={70} y={70} textAnchor="middle" dominantBaseline="central"
                    style={{ transform: "rotate(90deg)", transformOrigin: "70px 70px" }}
                    fill={textPri} fontSize={22} fontWeight={900} fontFamily="Nunito, sans-serif">
                    {accuracy}%
                  </text>
                </svg>
              </div>
              <div style={{ color: textSec, fontSize: 13, marginTop: 10 }}>
                Based on {stats?.total_questions || 0} questions answered
              </div>
            </div>

            {/* Next Certificate Progress */}
            {nextCertDef && (
              <div style={{
                background: surface, borderRadius: 20, padding: "22px 24px",
                border: `1px solid ${border}`,
                boxShadow: D ? "0 2px 16px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
              }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: textPri, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Medal size={18} color="#f59e0b" />
                  Next Certificate
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                  <span style={{ fontSize: 36 }}>{CERT_THEMES[nextCertDef.level]?.badge}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: textPri }}>{nextCertDef.name}</div>
                    <div style={{ fontSize: 13, color: textSec, marginTop: 2 }}>
                      {Math.max(0, nextCertDef.threshold - uniqueAnswered)} unique questions to go
                    </div>
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#10b981" }}>{nextCertProgress}%</div>
                </div>
                {/* Progress bar */}
                <div style={{ height: 10, background: surface2, borderRadius: 100, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${nextCertProgress}%`,
                    background: "linear-gradient(90deg, #10b981, #0d9488)",
                    borderRadius: 100, transition: "width 1s ease",
                  }} />
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: textSec, marginTop: 6 }}>
                  {uniqueAnswered} / {nextCertDef.threshold} questions
                </div>
              </div>
            )}

            {/* Performance by Difficulty */}
            {progress?.difficulty_breakdown && (
              <div style={{
                background: surface, borderRadius: 20, padding: "22px 24px",
                border: `1px solid ${border}`,
                boxShadow: D ? "0 2px 16px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
              }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: textPri, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                  <TrendingUp size={18} color="#10b981" />
                  Performance by Difficulty
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {[
                    { key: "easy",       label: "Basic",    barColor: "#10b981", trackColor: "#d1fae5" },
                    { key: "medium",     label: "Moderate", barColor: "#f59e0b", trackColor: "#fef3c7" },
                    { key: "hard",       label: "Advanced", barColor: "#f43f5e", trackColor: "#ffe4e6" },
                    { key: "super_hard", label: "Expert",   barColor: "#a855f7", trackColor: "#f3e8ff" },
                  ].map(({ key, label, barColor, trackColor }) => {
                    const data = progress.difficulty_breakdown[key];
                    const pct = data?.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
                    return (
                      <div key={key}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: textPri }}>{label}</span>
                          <span style={{ fontSize: 13, color: textSec }}>
                            {data?.correct || 0}/{data?.total || 0} &nbsp;({pct}%)
                          </span>
                        </div>
                        <div style={{ height: 8, background: D ? surface2 : trackColor, borderRadius: 100, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", width: `${pct}%`,
                            background: barColor, borderRadius: 100,
                            transition: "width 0.8s ease",
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Areas to Improve */}
            <div style={{
              background: surface, borderRadius: 20, padding: "22px 24px",
              border: `1px solid ${border}`,
              boxShadow: D ? "0 2px 16px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
            }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: textPri, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Target size={18} color="#f59e0b" />
                Areas to Improve
              </div>
              {progress?.weak_topics && progress.weak_topics.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {progress.weak_topics.map((topic, idx) => (
                    <div key={idx} style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      background: D ? "#2d1f0a" : "#fffbeb",
                      border: `1px solid ${D ? "#78350f" : "#fde68a"}`,
                      borderRadius: 100, padding: "7px 14px",
                      fontSize: 13,
                    }}>
                      <span style={{ fontWeight: 700, color: textPri, textTransform: "capitalize" }}>
                        {topic.topic?.replace(/_/g, " ")}
                      </span>
                      <span style={{ fontWeight: 800, color: "#d97706" }}>{topic.accuracy}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "20px 0", color: textSec, fontSize: 15 }}>
                  Great work! No weak areas 🎉
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            ACHIEVEMENTS TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "achievements" && (
          <motion.div key="achievements" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14,
            }}>
              {ALL_CERTS.map((certDef, idx) => {
                const isEarned = earnedLevels.has(certDef.level);
                const colors = CERT_THEMES[certDef.level];
                const earnedCert = (progress?.certificates_earned || []).find(c => c.level === certDef.level);
                const needed = Math.max(0, certDef.threshold - uniqueAnswered);

                const pct = Math.min(100, Math.round((uniqueAnswered / certDef.threshold) * 100));
                return (
                  <div
                    key={idx}
                    onClick={() => isEarned && openCertModal(earnedCert || certDef)}
                    style={{
                      background: surface,
                      borderRadius: 18,
                      padding: "20px 16px",
                      border: isEarned ? `2px solid ${colors.border}` : `1px solid ${border}`,
                      cursor: isEarned ? "pointer" : "default",
                      boxShadow: isEarned ? `0 0 20px ${colors.border}55` : "none",
                      textAlign: "center",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      position: "relative", overflow: "hidden",
                    }}
                    onMouseEnter={e => { if (isEarned) e.currentTarget.style.transform = "scale(1.03)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
                  >
                    {/* Badge emoji */}
                    <div style={{ fontSize: 38, marginBottom: 8 }}>
                      {isEarned ? (certDef.emoji || colors.badge) : "🔒"}
                    </div>

                    {/* Name */}
                    <div style={{ fontWeight: 900, fontSize: 14, color: isEarned ? textPri : textSec, marginBottom: 4 }}>
                      {certDef.name}
                    </div>

                    {/* Threshold label */}
                    <div style={{ fontSize: 11, color: textSec, marginBottom: 10 }}>
                      {certDef.threshold} unique questions
                    </div>

                    {isEarned ? (
                      <>
                        <div style={{ fontSize: 12, color: "#10b981", fontWeight: 800, marginBottom: 6 }}>✅ Earned!</div>
                        <div style={{ fontSize: 11, color: colors.border, fontWeight: 700, background: D ? "#0f172a" : "#f0fdf4", borderRadius: 100, padding: "3px 10px", display: "inline-block" }}>
                          Tap to download →
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Progress bar */}
                        <div style={{ height: 5, background: D ? "#334155" : "#e2e8f0", borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: colors.border, borderRadius: 4, transition: "width 0.6s ease" }} />
                        </div>
                        <div style={{ fontSize: 11, color: textSec }}>
                          {uniqueAnswered}/{certDef.threshold} · {needed} more to go
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            QUIZ HISTORY TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === "history" && (
          <motion.div key="history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {stats?.recent_quizzes && stats.recent_quizzes.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {stats.recent_quizzes.map((quiz, index) => {
                  const acc = Math.round((quiz.correct_count / (quiz.total_questions || 15)) * 100);
                  const scoreColor = quiz.correct_count >= 12 ? "#10b981" : quiz.correct_count >= 8 ? "#f59e0b" : "#f43f5e";
                  const scoreBg = quiz.correct_count >= 12
                    ? (D ? "#0d2e1f" : "#d1fae5")
                    : quiz.correct_count >= 8
                    ? (D ? "#2d1f0a" : "#fef3c7")
                    : (D ? "#2d0a0f" : "#ffe4e6");
                  const isExpanded = selectedQuiz === quiz.id;

                  return (
                    <div key={index} style={{
                      background: surface, borderRadius: 18,
                      border: `1px solid ${border}`,
                      overflow: "hidden",
                      boxShadow: D ? "0 2px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      {/* Row */}
                      <div
                        data-testid={`quiz-history-${index}`}
                        onClick={() => fetchQuizDetail(quiz.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 14,
                          padding: "16px 18px", cursor: "pointer",
                        }}
                      >
                        {/* Score badge */}
                        <div style={{
                          width: 52, height: 52, borderRadius: 14,
                          background: scoreBg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <span style={{ fontWeight: 900, fontSize: 14, color: scoreColor }}>
                            {quiz.correct_count}/{quiz.total_questions || 15}
                          </span>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: textPri, marginBottom: 3 }}>
                            Income Tax Quiz
                          </div>
                          <div style={{ fontSize: 12, color: textSec }}>
                            📅 {new Date(quiz.completed_at.endsWith("Z") ? quiz.completed_at : quiz.completed_at + "Z").toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })} &nbsp;
                            🕐 {new Date(quiz.completed_at.endsWith("Z") ? quiz.completed_at : quiz.completed_at + "Z").toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}
                          </div>
                          {/* Accuracy bar */}
                          <div style={{ height: 4, background: surface2, borderRadius: 100, marginTop: 8, overflow: "hidden" }}>
                            <div style={{
                              height: "100%", width: `${acc}%`,
                              background: scoreColor, borderRadius: 100,
                            }} />
                          </div>
                        </div>

                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontWeight: 900, fontSize: 14, color: "#f59e0b" }}>
                            +{quiz.gp_earned} GP
                          </div>
                          <div style={{ fontSize: 12, color: textSec, marginTop: 2 }}>{acc}%</div>
                          <Eye size={14} color={textSec} style={{ marginTop: 4 }} />
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div style={{
                          borderTop: `1px solid ${border}`,
                          padding: "16px 18px",
                          background: surface2,
                        }}>
                          {loadingQuiz ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {[1, 2, 3].map(i => (
                                <div key={i} style={{
                                  height: 56, borderRadius: 10,
                                  background: D ? "#334155" : "#e2e8f0",
                                  animation: "pulse 1.5s infinite",
                                }} />
                              ))}
                            </div>
                          ) : quizDetail && (
                            <div>
                              {/* Summary bar */}
                              <div style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", marginBottom: 14,
                                background: surface, borderRadius: 12, padding: "10px 14px",
                              }}>
                                <div>
                                  <span style={{ fontWeight: 700, color: textPri, fontSize: 14 }}>{quizDetail.topic_name}</span>
                                  <div style={{ fontSize: 12, color: textSec, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                                    <Clock size={11} /> {new Date(quizDetail.completed_at.endsWith("Z") ? quizDetail.completed_at : quizDetail.completed_at + "Z").toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" })}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <span style={{ fontWeight: 900, fontSize: 18, color: scoreColor }}>
                                    {quizDetail.correct_count}/{quizDetail.total_questions}
                                  </span>
                                  <div style={{ fontSize: 12, color: "#f59e0b", fontWeight: 700 }}>+{quizDetail.gp_earned} GP</div>
                                </div>
                              </div>

                              {/* Questions */}
                              {(quizDetail.questions || quizDetail.answers || []).length === 0 ? (
                                <div style={{
                                  textAlign: "center", padding: "24px 0",
                                  background: surface, borderRadius: 12, color: textSec, fontSize: 14,
                                }}>
                                  <div style={{ fontSize: 22, marginBottom: 8 }}>📋</div>
                                  <div style={{ fontWeight: 700, color: textPri }}>Detailed review not available</div>
                                  <div style={{ fontSize: 12, marginTop: 4 }}>New quizzes will have full question-by-question review.</div>
                                </div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                  {(quizDetail.questions || quizDetail.answers || []).map((q, idx) => (
                                    <div key={idx} style={{
                                      borderRadius: 12, padding: "14px 16px",
                                      border: `2px solid ${q.is_correct ? "#10b981" : "#f43f5e"}`,
                                      background: q.is_correct
                                        ? (D ? "#0d2e1f" : "#f0fdf4")
                                        : (D ? "#2d0a0f" : "#fff1f2"),
                                    }}>
                                      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                        {q.is_correct
                                          ? <CheckCircle size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 1 }} />
                                          : <XCircle size={18} color="#f43f5e" style={{ flexShrink: 0, marginTop: 1 }} />}
                                        <span style={{ fontSize: 14, fontWeight: 600, color: textPri }}>
                                          <span style={{ color: textSec, marginRight: 6 }}>Q{q.question_number || idx + 1}.</span>
                                          {q.question || <em style={{ color: textSec }}>Question not available</em>}
                                        </span>
                                      </div>

                                      <div style={{ marginLeft: 28, display: "flex", flexDirection: "column", gap: 6 }}>
                                        {(q.options || []).map((option, optIdx) => {
                                          const isCorrectOption = optIdx === q.correct_answer || option === q.correct;
                                          const isUserWrongOption = (optIdx === q.user_answer || option === q.selected) && !q.is_correct;
                                          return (
                                            <div key={optIdx} style={{
                                              padding: "6px 12px", borderRadius: 8, fontSize: 13,
                                              background: isCorrectOption
                                                ? (D ? "#0d3a21" : "#dcfce7")
                                                : isUserWrongOption
                                                ? (D ? "#3a0a0d" : "#fee2e2")
                                                : surface,
                                              color: isCorrectOption ? "#059669"
                                                : isUserWrongOption ? "#dc2626"
                                                : textSec,
                                              fontWeight: isCorrectOption ? 700 : 400,
                                              textDecoration: isUserWrongOption ? "line-through" : "none",
                                            }}>
                                              {String.fromCharCode(65 + optIdx)}. {option}
                                              {isCorrectOption && <span style={{ marginLeft: 8, fontSize: 11 }}>(Correct)</span>}
                                              {isUserWrongOption && <span style={{ marginLeft: 8, fontSize: 11 }}>(Your answer)</span>}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {q.explanation && (
                                        <div style={{
                                          marginLeft: 28, marginTop: 10, padding: "10px 12px",
                                          background: surface, borderRadius: 10, fontSize: 13, color: textSec,
                                        }}>
                                          <span style={{ fontWeight: 700, color: textPri }}>Explanation: </span>
                                          {q.explanation}
                                          {q.reference_url && !q.is_correct && (
                                            <div style={{ marginTop: 6 }}>
                                              <a
                                                href={q.reference_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#10b981", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                                              >
                                                <ExternalLink size={12} /> Learn more from official source
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                background: surface, borderRadius: 20, padding: "48px 24px",
                border: `1px solid ${border}`, textAlign: "center",
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: textPri, marginBottom: 8 }}>No quizzes taken yet</div>
                <div style={{ color: textSec, fontSize: 14, marginBottom: 24 }}>Start your first quiz to see your history here.</div>
                <button
                  data-testid="start-quiz-btn"
                  onClick={() => navigate("/quiz")}
                  style={{
                    background: "#10b981", color: "white",
                    border: "none", borderRadius: 100, padding: "12px 32px",
                    fontWeight: 800, fontSize: 15, cursor: "pointer",
                    fontFamily: "'Nunito', sans-serif",
                  }}
                >
                  Take Your First Quiz
                </button>
              </div>
            )}
          </motion.div>
        )}
      </main>

      {/* ═══════════════════════════════════════════════════════════════════════
          CERTIFICATE MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {certModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setCertModal(null); }}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "16px", overflowY: "auto",
          }}
        >
          <div style={{
            background: surface, borderRadius: 24,
            width: "100%", maxWidth: 680,
            boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
          }}>
            {/* Modal header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "20px 24px", borderBottom: `1px solid ${border}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Trophy size={22} color="#f59e0b" />
                <span style={{ fontWeight: 800, fontSize: 18, color: textPri }}>Your Certificate</span>
              </div>
              <button
                onClick={() => setCertModal(null)}
                style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: surface2, border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <X size={18} color={textSec} />
              </button>
            </div>

            {/* Canvas */}
            <div style={{ padding: "16px", overflowX: "auto" }}>
              <canvas
                ref={certCanvasRef}
                width={800}
                height={660}
                style={{
                  width: "100%", maxWidth: "100%",
                  borderRadius: 12,
                  border: `1px solid ${border}`,
                  boxShadow: D ? "0 2px 20px rgba(0,0,0,0.4)" : "0 2px 16px rgba(0,0,0,0.1)",
                }}
              />
            </div>

            {/* Actions */}
            <div style={{
              display: "flex", gap: 12, padding: "16px 24px 24px",
              borderTop: `1px solid ${border}`,
              flexWrap: "wrap",
            }}>
              <button
                onClick={downloadCertificate}
                style={{
                  flex: 1, minWidth: 140,
                  background: "#10b981", color: "white",
                  border: "none", borderRadius: 100, padding: "13px 24px",
                  fontWeight: 800, fontSize: 15, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                <Download size={18} />
                Download PNG
              </button>
              <button
                onClick={() => setCertModal(null)}
                style={{
                  flex: 1, minWidth: 120,
                  background: "transparent", color: textPri,
                  border: `1.5px solid ${border}`, borderRadius: 100, padding: "13px 24px",
                  fontWeight: 700, fontSize: 15, cursor: "pointer",
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
